import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LifeBuoy } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";

/**
 * FALAR COM O WELFARE OFFICER — o caminho que não existia.
 *
 * Quando o Espelho não sabe, o morador hoje não tem para onde ir DENTRO do
 * VYV. Este chip é a saída, e ele é o evento que faz a métrica oficial do
 * piloto existir: sem escalada não há contrafactual, e "perguntas resolvidas
 * sem um Welfare Officer tocar" vira chute.
 *
 * 🔴 A FRASE DE CONSENTIMENTO É A PEÇA MAIS IMPORTANTE DESTE ARQUIVO, e ela
 * aparece ANTES de qualquer campo de texto. A escalada é uma exceção declarada
 * ao piso de privacidade n>=5 do mundo do parceiro — a única linha individual
 * que o parceiro vê. Essa exceção SÓ se sustenta porque o morador soube, antes
 * de escrever, que uma pessoa da acomodação dele vai ler. Sem a frase não há
 * exceção: há vazamento.
 * ⛔ A MESMA frase está em PartnerPrivacy no repositório do portal
 * (`partnerWorld.privacy.consentLine`). Se as duas divergirem, o consentimento
 * deixa de ser informado — mudar aqui é mudar lá, no mesmo dia.
 *
 * ⛔ O QUE VIAJA: só o que a pessoa escreve NESTE campo, mais categoria, canal
 * e horário. Nada de histórico de conversa, humor, sono ou check-in — o
 * sistema não vai buscar (GDPR art. 9). `session_id` acompanha para ligar de
 * volta à conversa em auditoria, e NÃO é exposto na fila do parceiro. Desde a
 * task 9de48f01 viajam também `origin` (quem chamou o chip — a métrica do
 * objetivo #1 precisa distinguir Espelho de Meu Trabalho) e, só no MyWork,
 * `work_checkin_id` (a pergunta exata) — ambos com o mesmo contrato de
 * exposição do session_id: agregado sim, fila não.
 *
 * ⚠️ COBERTURA DE CANAL, dita e não fingida: isto cobre o canal `app`. O
 * morador que conversa por WHATSAPP não tem botão nenhum — lá não há
 * superfície onde pendurar um chip. Por isso o WhatsApp está EXCLUÍDO do
 * denominador da métrica nesta passada (a tela do parceiro diz isso). Cobrir
 * WhatsApp por palavra-chave é frente própria.
 *
 * Visualmente segue o WaSoftAskChip: card escuro, borda tênue, inline no fio.
 * ⛔ Não herda o MirrorActionChip — aquele é dirigido por `proposal.tool` e
 * pressupõe proposta do modelo; este nasce de um toque da pessoa.
 */

const CATEGORIES = ["housing", "contract", "work", "other"] as const;
type Category = (typeof CATEGORIES)[number];

const MAX_CHARS = 1000;

interface WelfareOfficerChipProps {
  /** Liga a escalada de volta à conversa. Auditoria — não vai para a fila. */
  sessionId: string | null;
  /** 🔴 De onde a escalada parte — carimbado pelo CHAMADOR, nunca inferido
      (task 9de48f01, A3): 'mirror' no chat do Espelho, 'my_work' no módulo
      Meu Trabalho — inclusive o botão mywork.answer.notResolved da resposta
      entregue (#513 fix 2a). Obrigatório de propósito: prop opcional viraria
      'unknown' em silêncio no próximo chamador, e o default do banco é rede
      de segurança, não caminho. É o que faz "o Espelho respondeu e ainda
      precisou de humano" sair por igualdade em vez de join temporal. */
  origin: "mirror" | "my_work";
  /** A pergunta exata (work_checkins.id) que esta escalada não resolveu —
      decisão B2·a da task: o vínculo por IGUALDADE é o que torna
      work_escalated_after_answer exato em vez de aproximado. Só o MyWork
      passa; no chat do Espelho fica de fora (lá o vínculo é sessionId).
      ⛔ Não exposto na fila do parceiro — mesmo contrato de session_id. */
  workCheckinId?: string | null;
  onSent: () => void;
  onDismiss: () => void;
}

export default function WelfareOfficerChip({
  sessionId,
  origin,
  workCheckinId,
  onSent,
  onDismiss,
}: WelfareOfficerChipProps) {
  const { t } = useLanguage();
  const [category, setCategory] = useState<Category>("housing");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  const canSend = body.trim().length > 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setFailed(false);
    // ⛔ org_id e site_id NÃO são enviados daqui de propósito: um trigger no
    // banco os carimba a partir de organization_members. Se viessem do
    // cliente, um morador poderia cair na fila da unidade errada.
    //
    // ⚠️ O `as any` é DÍVIDA DECLARADA, com data de vencimento: os tipos
    // gerados em integrations/supabase/types.ts não conhecem NENHUMA tabela de
    // organização (medido em 17/08: zero ocorrências de "organization" no
    // arquivo), e resident_escalations nasce nesta trilha — o .sql ainda nem
    // foi aplicado. É o mesmo padrão de `.from("projects" as any)` já em uso
    // aqui. SAI no dia em que os tipos forem regerados depois do apply.
    const userId = (await supabase.auth.getUser()).data.user?.id;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tabela nova, fora dos tipos gerados; sai com a regeneração pós-apply (ver acima)
    const { data, error } = await (supabase.from("resident_escalations" as any) as any)
      .insert({
        user_id: userId,
        session_id: sessionId,
        channel: "app",
        category,
        body: body.trim(),
        origin,
        work_checkin_id: workCheckinId ?? null,
      })
      .select();
    setSending(false);
    // Regra 4.9 também no INSERT: com RLS, negação volta 0 linhas SEM erro —
    // 0 linhas é falha, nunca sucesso presumido. (O insert original de #486
    // não tinha a guarda; entra agora que o arquivo é tocado.)
    if (error || !data || data.length === 0) {
      console.error("[WelfareOfficerChip] escalation insert failed:", error ?? "0 rows returned");
      setFailed(true);
      return;
    }
    onSent();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="max-w-[86%] rounded-2xl rounded-bl-sm p-3.5 mt-1.5"
      style={{
        background: "hsl(222 18% 11%)",
        border: "1px solid hsl(187 72% 50% / 0.22)",
      }}
    >
      <div className="flex items-start gap-2.5 mb-2.5">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: "hsl(187 72% 50% / 0.14)",
            border: "1px solid hsl(187 72% 50% / 0.28)",
          }}
        >
          <LifeBuoy size={15} style={{ color: "hsl(187 72% 65%)" }} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-display font-medium text-foreground/90 leading-snug">
            {t("welfareOfficer.title")}
          </p>
          {/* 🔴 O CONSENTIMENTO VEM ANTES DO CAMPO, sempre. Ver o cabeçalho. */}
          <p className="text-[11px] font-body text-foreground/70 leading-relaxed mt-1">
            {t("welfareOfficer.consent")}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2.5">
        {CATEGORIES.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setCategory(key)}
            aria-pressed={category === key}
            className="text-[11px] font-display px-2.5 py-1 rounded-full transition-colors"
            style={
              category === key
                ? {
                    background: "hsl(187 72% 50% / 0.18)",
                    color: "hsl(187 72% 75%)",
                    border: "1px solid hsl(187 72% 50% / 0.4)",
                  }
                : {
                    background: "hsl(222 18% 16% / 0.6)",
                    color: "hsl(210 16% 70%)",
                    border: "1px solid hsl(222 18% 30% / 0.6)",
                  }
            }
          >
            {t(`welfareOfficer.category.${key}`)}
          </button>
        ))}
      </div>

      <label className="sr-only" htmlFor="welfare-officer-body">
        {t("welfareOfficer.placeholder")}
      </label>
      <textarea
        id="welfare-officer-body"
        value={body}
        maxLength={MAX_CHARS}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder={t("welfareOfficer.placeholder")}
        className="w-full rounded-xl p-2.5 text-[13px] font-body text-foreground/90 resize-none"
        style={{
          background: "hsl(222 18% 8% / 0.7)",
          border: "1px solid hsl(222 18% 30% / 0.6)",
        }}
      />

      {failed && (
        <p role="alert" className="text-[11px] font-body mt-1.5" style={{ color: "hsl(0 70% 68%)" }}>
          {t("welfareOfficer.error")}
        </p>
      )}

      <div className="flex gap-2 mt-2.5">
        <button
          type="button"
          onClick={() => void send()}
          disabled={!canSend}
          className="flex-1 rounded-xl font-display font-semibold text-[12px] tracking-wide transition-all active:scale-[0.98] disabled:opacity-50"
          style={{ minHeight: 44, background: "hsl(187 72% 50%)", color: "hsl(222 38% 6%)" }}
        >
          {sending ? (
            <span className="inline-flex items-center justify-center gap-1.5">
              <Loader2 size={13} className="animate-spin" />
              {t("welfareOfficer.sending")}
            </span>
          ) : (
            t("welfareOfficer.send")
          )}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={sending}
          className="flex-1 rounded-xl font-display font-medium text-[12px] tracking-wide transition-all active:scale-[0.98] disabled:opacity-50"
          style={{
            minHeight: 44,
            background: "hsl(222 18% 18% / 0.6)",
            color: "hsl(210 20% 88%)",
            border: "1px solid hsl(222 18% 30% / 0.6)",
          }}
        >
          {t("welfareOfficer.cancel")}
        </button>
      </div>
    </motion.div>
  );
}
